"use client";

import { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faXmark,
  faCog,
  faPaintBrush,
} from "@fortawesome/free-solid-svg-icons";
import ConfigSettings from "@/components/widgets/settings/ConfigSettings";
import LayoutSettings from "@/components/widgets/settings/LayoutSettings";

export function MobileSettingsDrawer({
  isOpen,
  onClose,
  widget,
  settingsType,
  onLayoutChange,
  onConfigChange,
  previewMode,
  allWidgets,
}) {
  const [isAnimating, setIsAnimating] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const drawerRef = useRef(null);
  const startTouchY = useRef(0);
  const currentTouchY = useRef(0);

  // Handle open animation
  useEffect(() => {
    if (isOpen && !isClosing) {
      setIsAnimating(true);
      // Prevent body scroll
      document.body.style.overflow = "hidden";

      // Small delay for backdrop to appear, then slide in drawer
      const openTimer = setTimeout(() => {
        setIsVisible(true);
      }, 50);

      // Mark animation complete
      const completeTimer = setTimeout(() => setIsAnimating(false), 450);

      return () => {
        clearTimeout(openTimer);
        clearTimeout(completeTimer);
      };
    }
  }, [isOpen, isClosing]);

  // Reset state when closed
  useEffect(() => {
    if (!isOpen && !isClosing) {
      setIsVisible(false);
      setIsAnimating(false);
    }
  }, [isOpen, isClosing]);

  // Handle close with proper cleanup
  const handleClose = useCallback(() => {
    if (isClosing) return; // Prevent double-close

    setIsClosing(true);
    setIsVisible(false); // Start slide-out animation

    // Wait for animation to complete
    setTimeout(() => {
      setIsClosing(false);
      document.body.style.overflow = "";
      onClose();
    }, 350);
  }, [onClose, isClosing]);

  // Simple swipe down to close
  const handleTouchStart = useCallback((e) => {
    // Only handle touches on the handle bar area
    if (!e.target.closest(".drawer-handle")) return;

    startTouchY.current = e.touches[0].clientY;
    currentTouchY.current = e.touches[0].clientY;
  }, []);

  const handleTouchMove = useCallback((e) => {
    if (startTouchY.current === 0) return;

    currentTouchY.current = e.touches[0].clientY;
    const deltaY = currentTouchY.current - startTouchY.current;

    // Only allow downward swipes and apply minimal resistance
    if (deltaY > 0) {
      const resistance = Math.min(deltaY * 0.3, 100); // Cap at 100px
      if (drawerRef.current) {
        // Remove transition during dragging for smooth follow
        drawerRef.current.style.transition = "none";
        drawerRef.current.style.transform = `translateY(${resistance}px) scale(${1 - resistance * 0.001})`;
      }
    }
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (startTouchY.current === 0) return;

    const deltaY = currentTouchY.current - startTouchY.current;

    // Reset touch tracking
    startTouchY.current = 0;
    currentTouchY.current = 0;

    // If swiped down more than 80px, close the drawer
    if (deltaY > 80) {
      handleClose();
    } else {
      // Snap back to original position with smooth animation
      if (drawerRef.current) {
        drawerRef.current.style.transform = "translateY(0) scale(1)";
        drawerRef.current.style.transition =
          "transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)";
      }
    }
  }, [handleClose]);

  // Add touch listeners
  useEffect(() => {
    if (!isOpen) return;

    const drawer = drawerRef.current;
    if (!drawer) return;

    drawer.addEventListener("touchstart", handleTouchStart, { passive: true });
    drawer.addEventListener("touchmove", handleTouchMove, { passive: true });
    drawer.addEventListener("touchend", handleTouchEnd, { passive: true });

    return () => {
      drawer.removeEventListener("touchstart", handleTouchStart);
      drawer.removeEventListener("touchmove", handleTouchMove);
      drawer.removeEventListener("touchend", handleTouchEnd);
    };
  }, [isOpen, handleTouchStart, handleTouchMove, handleTouchEnd]);

  // Config update wrapper
  const handleConfigUpdate = useCallback(
    (widgetId, updates) => {
      if (updates && updates.config) {
        onConfigChange(updates.config);
      } else {
        onConfigChange(updates);
      }
    },
    [onConfigChange]
  );

  // Layout update wrapper
  const handleLayoutUpdate = useCallback(
    (widgetId, updates) => {
      if (updates && updates.layout) {
        onLayoutChange(
          updates.layout,
          updates.isRealTimeUpdate ? { isRealTimeUpdate: true } : undefined
        );
      } else {
        onLayoutChange(updates);
      }
    },
    [onLayoutChange]
  );

  // Don't render anything if not open
  if (!isOpen && !isClosing) return null;

  return createPortal(
    <div className="fixed inset-0 z-[70] flex items-end">
      {/* Backdrop */}
      <div
        className={`absolute inset-0 bg-black/50 transition-opacity duration-300 ease-out ${
          isVisible ? "opacity-100" : "opacity-0"
        }`}
        onClick={handleClose}
      />

      {/* Drawer */}
      <div
        ref={drawerRef}
        className={`relative w-full bg-gray-900 rounded-t-3xl shadow-2xl 
          max-h-[85vh] min-h-[50vh] flex flex-col
          transform transition-all duration-350
          ${isVisible ? "translate-y-0 scale-100" : "translate-y-full scale-95"}`}
        style={{
          background: "linear-gradient(135deg, #1f2937 0%, #111827 100%)",
          borderTop: "1px solid rgba(255, 255, 255, 0.1)",
          transformOrigin: "bottom center",
          transitionTimingFunction: isVisible
            ? "cubic-bezier(0.34, 1.56, 0.64, 1)" // Spring effect on open
            : "cubic-bezier(0.25, 0.46, 0.45, 0.94)", // Smooth on close
        }}
      >
        {/* Handle bar */}
        <div className="drawer-handle flex-shrink-0 flex justify-center py-4 cursor-pointer">
          <div className="w-12 h-1 bg-white/30 rounded-full" />
        </div>

        {/* Header */}
        <div className="flex-shrink-0 px-6 pb-4 border-b border-white/10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center">
                <FontAwesomeIcon
                  icon={settingsType === "config" ? faCog : faPaintBrush}
                  className="h-4 w-4 text-blue-400"
                />
              </div>
              <h2 className="text-lg font-semibold text-white">
                {settingsType === "config"
                  ? "Widget Settings"
                  : "Layout & Style"}
              </h2>
            </div>
            <button
              onClick={handleClose}
              className="w-8 h-8 rounded-lg bg-white/10 hover:bg-white/20 
                         flex items-center justify-center transition-colors"
            >
              <FontAwesomeIcon
                icon={faXmark}
                className="h-4 w-4 text-white/70"
              />
            </button>
          </div>
        </div>

        {/* Content */}
        <div
          className="flex-1 overflow-y-auto scrollbar-thin scrollbar-track-transparent 
                        scrollbar-thumb-white/20 px-6 py-6"
        >
          <div className="space-y-6">
            {settingsType === "config" ? (
              <ConfigSettings
                widget={widget}
                handleWidgetUpdate={handleConfigUpdate}
              />
            ) : (
              <LayoutSettings
                widget={widget}
                handleWidgetUpdate={handleLayoutUpdate}
                allWidgets={allWidgets}
              />
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default MobileSettingsDrawer;
